import { ref } from 'vue';
import { favoriteService } from '@/services/api';
import type { Favorite } from '@/services/api';

const favorites = ref<Set<string>>(new Set());
const initialized = ref(false);
const loading = ref(false);

function makeKey(service: string, objectId: string): string {
  return `${service}:${objectId}`;
}

export function useFavorites() {
  async function fetchFavorites() {
    if (initialized.value) return;
    loading.value = true;
    try {
      const list = await favoriteService.list();
      favorites.value = new Set(list.map((f: Favorite) => makeKey(f.service, f.objectId)));
      initialized.value = true;
    } finally {
      loading.value = false;
    }
  }

  function isFavorited(service: string, objectId: string): boolean {
    return favorites.value.has(makeKey(service, objectId));
  }

  async function toggleFavorite(service: string, objectId: string): Promise<void> {
    const key = makeKey(service, objectId);
    const wasFavorited = favorites.value.has(key);

    // Optimistic update
    const newSet = new Set(favorites.value);
    if (wasFavorited) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    favorites.value = newSet;

    try {
      if (wasFavorited) {
        await favoriteService.remove(service, objectId);
      } else {
        await favoriteService.create(service, objectId);
      }
    } catch (error) {
      // Revert on failure
      const revertSet = new Set(favorites.value);
      if (wasFavorited) {
        revertSet.add(key);
      } else {
        revertSet.delete(key);
      }
      favorites.value = revertSet;
      throw error;
    }
  }

  function getFavoritedIds(service: string): Set<string> {
    const prefix = `${service}:`;
    const ids = new Set<string>();
    for (const key of favorites.value) {
      if (key.startsWith(prefix)) {
        ids.add(key.slice(prefix.length));
      }
    }
    return ids;
  }

  return {
    favorites,
    loading,
    fetchFavorites,
    isFavorited,
    toggleFavorite,
    getFavoritedIds,
  };
}
